/* eslint-disable */
import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import {
  Box,
  TextField,
  Button,
  IconButton,
  Stack,
  Typography,
  CircularProgress,
  useTheme,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { updateExistingDepartment } from "store/features/company/department/departmentActions";
import editDepartmentSvg from "assets/modals/edit.svg";

const EditDepartmentModal = ({ isOpen, onClose, department, onSuccess }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    code: "",
    budget: "",
    description: "",
    hod_id: "",
  });

  useEffect(() => {
    if (department) {
      setFormData({
        name: department.name || "",
        code: department.code || "",
        budget: department.budget || "",
        description: department.description || "",
        hod_id: department.hod_id || "",
      });
    }
  }, [department]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleBudgetChange = (e) => {
    const value = e.target.value;
    if (value === "" || /^\d*\.?\d*$/.test(value)) {
      setFormData((prev) => ({
        ...prev,
        budget: value,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !department) {
      return;
    }

    setLoading(true);
    const submitData = {
      name: formData.name,
      code: formData.code,
      hod_id: department.hod_id || null,
      budget: formData.budget ? parseFloat(formData.budget) : 0,
      description: formData.description,
    };

    const result = await dispatch(
      updateExistingDepartment(department.id, submitData),
    );

    if (result.success) {
      onSuccess();
      onClose();
    }
    setLoading(false);
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: theme.palette.mode === "dark" ? "#1e293b" : "#f9fafb",
      "& fieldset": {
        borderColor: theme.palette.mode === "dark" ? "#475569" : "#d1d5db",
      },
      "&:hover fieldset": {
        borderColor: "#f59e0b",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#f59e0b",
        borderWidth: "2px",
      },
    },
    "& .MuiInputLabel-root": {
      color: theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
      "&.Mui-focused": {
        color: "#f59e0b",
      },
    },
  };

  if (!department) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop with fade animation */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 1300,
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(252, 226, 181, 0.14)"
                  : "rgba(0, 0, 0, 0.6)",
              backdropFilter: "blur(4px)",
            }}
          />

          {/* Modal with slide-in from right animation */}
          <motion.div
            initial={{ x: "100%", opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: "100%", opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            style={{
              position: "fixed",
              right: 0,
              top: 0,
              zIndex: 1301,
              height: "100%",
              width: "100%",
              maxWidth: "600px",
              overflowY: "auto",
              backgroundColor:
                theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
              boxShadow: theme.shadows[24],
            }}
          >
            {/* Header */}
            <Box
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 10,
                borderBottom: `1px solid ${
                  theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
                }`,
                backgroundColor:
                  theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
                position: "relative",
              }}
            >
              <Box sx={{ p: 3, pr: 7 }}>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <Box flexShrink={0}>
                    <Box
                      component="img"
                      src={editDepartmentSvg}
                      alt="Edit Department"
                      sx={{
                        width: 80,
                        height: 80,
                        objectFit: "contain",
                        transition: "all 0.3s",
                        "&:hover": {
                          filter: "drop-shadow(0 4px 8px rgba(245,158,11,0.3))",
                        },
                      }}
                    />
                  </Box>
                  <Box flex={1}>
                    <Typography
                      variant="h5"
                      component="h2"
                      sx={{
                        fontWeight: "bold",
                        color:
                          theme.palette.mode === "dark" ? "#ffffff" : "#111827",
                      }}
                    >
                      Edit Department
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 0.5,
                        color:
                          theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                      }}
                    >
                      Update department information
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <IconButton
                onClick={onClose}
                sx={{
                  position: "absolute",
                  top: 20,
                  right: 20,
                  width: 36,
                  height: 36,
                  borderRadius: "8px",
                  color: theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "#f59e0b",
                    color: "#ffffff",
                    transform: "scale(1.05)",
                  },
                  "&:active": {
                    transform: "scale(0.95)",
                  },
                }}
              >
                <CloseIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>

            {/* Form */}
            <form onSubmit={handleSubmit}>
              <Box sx={{ p: 3 }}>
                <Stack spacing={3}>
                  <Typography
                    variant="body2"
                    sx={{
                      color:
                        theme.palette.mode === "dark" ? "#febc4ac6" : "#df9d1a",
                      mb: 1,
                    }}
                  >
                    Department ID: <strong>{department.id}</strong> | Current
                    Code: <strong>{department.code}</strong>
                  </Typography>

                  <TextField
                    fullWidth
                    label="Department Name "
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Enter department name"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                        backgroundColor:
                          theme.palette.mode === "dark" ? "#1e293b" : "#f9fafb",
                        "& fieldset": {
                          borderColor:
                            theme.palette.mode === "dark"
                              ? "#475569"
                              : "#d1d5db",
                        },
                        "&:hover fieldset": {
                          borderColor: "#f59e0b",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#f59e0b",
                          borderWidth: "2px",
                        },
                      },
                      "& .MuiInputLabel-root": {
                        color:
                          theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                        position: "relative",
                        transform: "none",
                        marginBottom: "8px",
                        display: "block",
                      },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: "#f59e0b",
                      },
                      "& .MuiInputLabel-asterisk": {
                        color: "#ef4444",
                      },
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />

                  <TextField
                    fullWidth
                    label="Department Code"
                    name="code"
                    value={formData.code}
                    onChange={handleChange}
                    placeholder="e.g., FIN001"
                    helperText="Unique code for the department"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                        backgroundColor:
                          theme.palette.mode === "dark" ? "#1e293b" : "#f9fafb",
                        "& fieldset": {
                          borderColor:
                            theme.palette.mode === "dark"
                              ? "#475569"
                              : "#d1d5db",
                        },
                        "&:hover fieldset": {
                          borderColor: "#f59e0b",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#f59e0b",
                          borderWidth: "2px",
                        },
                      },
                      "& .MuiInputLabel-root": {
                        color:
                          theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                        position: "relative",
                        transform: "none",
                        marginBottom: "8px",
                        display: "block",
                      },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: "#f59e0b",
                      },
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />

                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextField
                      fullWidth
                      label="HOD ID (Head of Department)"
                      name="hod_id"
                      type="number"
                      value={formData.hod_id}
                      onChange={handleChange}
                      placeholder="Enter employee ID"
                      helperText="Employee ID of the department head"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "#1e293b"
                              : "#f9fafb",
                          "& fieldset": {
                            borderColor:
                              theme.palette.mode === "dark"
                                ? "#475569"
                                : "#d1d5db",
                          },
                          "&:hover fieldset": {
                            borderColor: "#f59e0b",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#f59e0b",
                            borderWidth: "2px",
                          },
                        },
                        "& .MuiInputLabel-root": {
                          color:
                            theme.palette.mode === "dark"
                              ? "#9ca3af"
                              : "#6b7280",
                          position: "relative",
                          transform: "none",
                          marginBottom: "8px",
                          display: "block",
                        },
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: "#f59e0b",
                        },
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />

                    <TextField
                      fullWidth
                      label="Budget (₹)"
                      name="budget"
                      value={formData.budget}
                      onChange={handleBudgetChange}
                      placeholder="250000"
                      helperText="Annual budget for the department"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "#1e293b"
                              : "#f9fafb",
                          "& fieldset": {
                            borderColor:
                              theme.palette.mode === "dark"
                                ? "#475569"
                                : "#d1d5db",
                          },
                          "&:hover fieldset": {
                            borderColor: "#f59e0b",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#f59e0b",
                            borderWidth: "2px",
                          },
                        },
                        "& .MuiInputLabel-root": {
                          color:
                            theme.palette.mode === "dark"
                              ? "#9ca3af"
                              : "#6b7280",
                          position: "relative",
                          transform: "none",
                          marginBottom: "8px",
                          display: "block",
                        },
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: "#f59e0b",
                        },
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Stack>

                  <TextField
                    fullWidth
                    label="Description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    multiline
                    rows={4}
                    placeholder="Brief description of the department"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                        backgroundColor:
                          theme.palette.mode === "dark" ? "#1e293b" : "#f9fafb",
                        "& fieldset": {
                          borderColor:
                            theme.palette.mode === "dark"
                              ? "#475569"
                              : "#d1d5db",
                        },
                        "&:hover fieldset": {
                          borderColor: "#f59e0b",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#f59e0b",
                          borderWidth: "2px",
                        },
                      },
                      "& .MuiInputLabel-root": {
                        color:
                          theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                        position: "relative",
                        transform: "none",
                        marginBottom: "8px",
                        display: "block",
                      },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: "#f59e0b",
                      },
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Stack>
              </Box>

              <Box
                sx={{
                  p: 3,
                  borderTop: `1px solid ${
                    theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
                  }`,
                  display: "flex",
                  gap: 2,
                }}
              >
                <Button
                  onClick={onClose}
                  disabled={loading}
                  fullWidth
                  sx={{
                    borderRadius: "12px",
                    py: 1.2,
                    textTransform: "none",
                    fontWeight: 500,
                    borderColor:
                      theme.palette.mode === "dark" ? "#475569" : "#d1d5db",
                    color:
                      theme.palette.mode === "dark" ? "#d1d5db" : "#374151",
                    "&:hover": {
                      borderColor: "#f59e0b",
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? "rgba(245,158,11,0.1)"
                          : "#fffbeb",
                    },
                  }}
                  variant="outlined"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading || !formData.name.trim()}
                  fullWidth
                  sx={{
                    borderRadius: "12px",
                    py: 1.2,
                    textTransform: "none",
                    fontWeight: 500,
                    background:
                      "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                    color: "#ffffff",
                    boxShadow: "0 10px 15px -3px rgba(245, 158, 11, 0.25)",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                      transform: "scale(1.02)",
                      boxShadow: "0 20px 25px -5px rgba(245, 158, 11, 0.3)",
                    },
                    "&:disabled": {
                      opacity: 0.7,
                    },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} sx={{ color: "#ffffff" }} />
                  ) : (
                    "Update Department"
                  )}
                </Button>
              </Box>
            </form>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default EditDepartmentModal;
